import React, { Suspense, lazy, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Play, Newspaper, Users, Music, MapPin, MessageSquare, MessageCircle,
  Image as ImageIcon, Calendar, ArrowRight, Sparkles,
} from "lucide-react";
import { ALBUMS, SONGS } from "@/data/eiffel65";
import { usePlayer } from "@/context/PlayerContext";
import { useI18n, translateCountry } from "@/context/I18nContext";
import LiveStats from "@/components/LiveStats";
import ShareButtons from "@/components/ShareButtons";
import TranslatedText from "@/components/TranslatedText";
import HomeDashboard from "@/components/home/HomeDashboard";

// Lazy-load below-the-fold sections for performance
const HomeFanMap   = lazy(() => import("@/components/home/HomeFanMap"));
const HomeNewMembers = lazy(() => import("@/components/home/HomeNewMembers"));
const HomeEvents   = lazy(() => import("@/components/home/HomeEvents"));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BG = "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=2000&q=80";

const SectionFallback = ({ height = 280 }) => (
  <div className="glass rounded-3xl animate-pulse" style={{ height }} />
);

/* ────────────────────────────────────────────────────────────────────────── */
/*  HERO                                                                       */
/* ────────────────────────────────────────────────────────────────────────── */
function Hero() {
  const { playSong } = usePlayer();
  const { t } = useI18n();
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  return (
    <section data-testid="home-hero" className="relative overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(7,11,25,0.85) 0%, rgba(15,30,71,0.7) 50%, rgba(7,11,25,0.95) 100%), url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: `translate3d(${mouse.x * -8}px, ${mouse.y * -8}px, 0) scale(1.05)`,
          transition: "transform 0.6s ease-out",
        }}
      />
      {/* Neon orbs */}
      <div className="absolute -top-32 -right-24 w-[36rem] h-[36rem] rounded-full bg-[#00AFFF]/15 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-32 -left-24 w-[36rem] h-[36rem] rounded-full bg-[#3B7DFF]/10 blur-[140px] pointer-events-none" />

      {/* Animated light rays */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-60">
        <div className="absolute top-1/4 left-1/3 w-[2px] h-96 bg-gradient-to-b from-[#00AFFF] to-transparent rotate-12 animate-pulse" style={{ animationDuration: "4s" }} />
        <div className="absolute top-1/3 right-1/4 w-[2px] h-72 bg-gradient-to-b from-[#3B7DFF] to-transparent -rotate-12 animate-pulse" style={{ animationDuration: "5s", animationDelay: "1s" }} />
      </div>

      <div
        className="relative container mx-auto max-w-[1500px] px-4 sm:px-8 pt-12 pb-16 sm:pt-20 sm:pb-28 lg:pt-28 lg:pb-36"
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setMouse({ x: (e.clientX - r.left) / r.width - 0.5, y: (e.clientY - r.top) / r.height - 0.5 });
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass border border-[#00AFFF]/30 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00AFFF] animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#00AFFF] font-bold">
              {t("home.hero_eyebrow", "The Eiffel 65 Fan Community · Since 1998")}
            </span>
          </div>

          <h1 className="font-display font-black tracking-tighter leading-[0.92]">
            <span className="block text-4xl xs:text-5xl sm:text-6xl lg:text-8xl silver-text">EIFFEL 65</span>
            <span className="block text-2xl xs:text-3xl sm:text-4xl lg:text-6xl neon-text mt-2">
              EIFFEL 65 FAN COMMUNITY
            </span>
          </h1>

          <p className="mt-5 sm:mt-7 text-sm sm:text-base lg:text-lg text-white/75 font-body max-w-xl leading-relaxed">
            {t("home.hero_subtitle", "Die größte Community für alle Eiffel-65-Fans weltweit. Discografie, Forum, Live-Chat, Fan-Karte, Galerie & Events — alles an einem Ort.")}
          </p>

          <div className="mt-7 sm:mt-9 flex flex-wrap items-center gap-2 sm:gap-3">
            <Link
              to="/contact?type=news"
              data-testid="hero-cta-news"
              className="group inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-[#00AFFF]/60 px-4 sm:px-5 py-3 sm:py-3.5 rounded-full font-display font-bold text-[11px] sm:text-xs uppercase tracking-[0.18em] text-white backdrop-blur transition-all"
            >
              <Newspaper size={14} /> {t("home.cta_news", "News entdecken")}
              <ArrowRight size={12} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </Link>
            <Link
              to="/register"
              data-testid="hero-cta-community"
              className="group inline-flex items-center gap-2 bg-[#00AFFF] hover:bg-[#4DC3FF] text-[#020B1F] px-4 sm:px-5 py-3 sm:py-3.5 rounded-full font-display font-black text-[11px] sm:text-xs uppercase tracking-[0.18em] shadow-[0_0_30px_rgba(0,175,255,0.45)] hover:shadow-[0_0_50px_rgba(0,175,255,0.7)] hover:-translate-y-0.5 transition-all"
            >
              <Users size={14} /> {t("home.cta_join", "Community beitreten")}
              <ArrowRight size={12} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </Link>
            <button
              onClick={() => playSong("blue")}
              data-testid="hero-cta-music"
              className="group inline-flex items-center gap-2 bg-transparent hover:bg-[#FF3B6B]/10 border border-[#FF3B6B]/50 hover:border-[#FF3B6B] px-4 sm:px-5 py-3 sm:py-3.5 rounded-full font-display font-bold text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[#FF3B6B] transition-all"
            >
              <Play size={14} fill="currentColor" /> {t("home.cta_listen", "Musik anhören")}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 text-white/40 text-[9px] uppercase tracking-[0.4em] font-bold animate-bounce pointer-events-none">
        {t("home.scroll", "Scroll")}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  NEWS                                                                       */
/* ────────────────────────────────────────────────────────────────────────── */
function NewsSection() {
  const { t } = useI18n();
  const [news, setNews] = useState([]);
  useEffect(() => {
    axios.get(`${API}/news?limit=3`).then((r) => setNews((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  // Always show 3 cards: real news first, padded with curated fallback content
  const FALLBACK = [
    { id: "f1", title: "Bestiale wird Sommer-Hit 2026", body: "Der neueste Eiffel-65-Track erobert die Charts europaweit. Über 50 Millionen Streams in den ersten 6 Wochen.", date: "2026-01-12", image: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=800&q=80" },
    { id: "f2", title: "Reunion-Tour 2026 angekündigt", body: "Eiffel 65 kommt auf Welttournee — 32 Städte in Europa, USA und Lateinamerika. Vorverkauf startet im März.", date: "2026-01-05", image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?auto=format&fit=crop&w=800&q=80" },
    { id: "f3", title: "Blue (Da Ba Dee) — 27 Jahre", body: "Der ikonische Track wird remixed von Top-DJs der heutigen Szene zum Jubiläum.", date: "2026-01-01", image: "https://images.unsplash.com/photo-1518609878373-06d740f60d8b?auto=format&fit=crop&w=800&q=80" },
  ];
  const items = (news.length >= 3 ? news : [...news, ...FALLBACK]).slice(0, 3);

  return (
    <section data-testid="home-news" className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-10 sm:pt-16 lg:pt-20">
      <div className="flex items-end justify-between mb-6 sm:mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#00AFFF] mb-1 inline-flex items-center gap-2">
            <Newspaper size={11} /> {t("home.news_eyebrow", "Aktuelle News")}
          </div>
          <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl silver-text tracking-tighter">{t("home.news_title", "Was es Neues gibt")}</h2>
        </div>
        <Link
          to="/contact?type=news"
          data-testid="home-news-all"
          className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full border border-white/15 hover:border-[#00AFFF]/60 text-white/80 hover:text-[#00AFFF] text-[10px] sm:text-[11px] font-display font-bold uppercase tracking-wider transition"
        >
          Alle News anzeigen{" "}<ArrowRight size={11} />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
        {items.map((n, i) => (
          <motion.article
            key={n.id || i}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.45, delay: i * 0.08 }}
            data-testid={`home-news-${i}`}
            className="group glass glass-hover rounded-2xl overflow-hidden cursor-pointer"
          >
            <div className="aspect-[16/10] overflow-hidden bg-[#041A3A] relative">
              {n.image && (
                <img
                  src={n.image}
                  alt={n.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#020B1F]/90 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#020B1F]/80 backdrop-blur border border-[#00AFFF]/40 text-[#00AFFF] text-[9px] uppercase tracking-wider font-bold">
                <Calendar size={9} /> {n.date ? new Date(n.date).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
              </div>
            </div>
            <div className="p-4 sm:p-5">
              <h3 className="font-display font-black text-base sm:text-lg text-white mb-1.5 line-clamp-2 group-hover:text-[#00AFFF] transition">
                <TranslatedText text={n.title} as="span" dataTestId={`home-news-${i}-title`} />
              </h3>
              <p className="text-xs sm:text-sm text-white/55 font-body line-clamp-2">
                <TranslatedText text={n.body} as="span" dataTestId={`home-news-${i}-body`} />
              </p>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  MUSIC HIGHLIGHTS                                                           */
/* ────────────────────────────────────────────────────────────────────────── */
function MusicHighlights() {
  const { playSong } = usePlayer();
  const { t } = useI18n();
  // 5 highlight items per spec (mix of albums + iconic singles)
  const highlights = [
    { type: "album",  id: "europop",          title: "Europop",            sub: "1999 · Debut",     cover: ALBUMS.find((a) => a.id === "europop")?.cover,           link: "/albums" },
    { type: "album",  id: "contact",          title: "Contact!",           sub: "2001",             cover: ALBUMS.find((a) => a.id === "contact")?.cover,           link: "/albums" },
    { type: "song",   id: "blue",             title: "Blue (Da Ba Dee)",   sub: "1998 · The Anthem",cover: SONGS.find((s) => s.id === "blue")?.cover,               link: "/singles" },
    { type: "song",   id: "move-your-body",   title: "Move Your Body",     sub: "1999",             cover: SONGS.find((s) => s.id === "move-your-body")?.cover,     link: "/singles" },
    { type: "song",   id: "too-much-of-heaven", title: "Too Much Of Heaven", sub: "1999",          cover: SONGS.find((s) => s.id === "too-much-of-heaven")?.cover, link: "/singles" },
  ].filter((h) => h.cover);

  return (
    <section data-testid="home-music" className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-12 sm:pt-20">
      <div className="flex items-end justify-between mb-6 sm:mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#00AFFF] mb-1 inline-flex items-center gap-2">
            <Music size={11} /> {t("home.music_eyebrow", "Musik-Highlights")}
          </div>
          <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl silver-text tracking-tighter">{t("home.music_title", "Die Klassiker")}</h2>
        </div>
        <Link to="/discography" className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full border border-white/15 hover:border-[#00AFFF]/60 text-white/80 hover:text-[#00AFFF] text-[10px] sm:text-[11px] font-display font-bold uppercase tracking-wider transition">
          {t("home.music_all", "Komplette Discografie")} <ArrowRight size={11} />
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {highlights.map((h, i) => (
          <motion.div
            key={h.id}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            data-testid={`home-music-${h.id}`}
            className="group relative glass rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-transform duration-300"
          >
            <Link to={h.link} className="block">
              <div className="aspect-square overflow-hidden bg-[#041A3A]">
                <img src={h.cover} alt={h.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#020B1F] via-[#020B1F]/30 to-transparent opacity-90" />
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                <div className="text-[9px] uppercase tracking-[0.25em] text-[#00AFFF] mb-0.5">{h.type === "album" ? "ALBUM" : "SINGLE"}</div>
                <div className="font-display font-black text-white text-sm sm:text-base leading-tight">{h.title}</div>
                <div className="text-[10px] text-white/55 mt-0.5">{h.sub}</div>
              </div>
            </Link>
            {h.type === "song" && (
              <button
                onClick={(e) => { e.preventDefault(); playSong(h.id); }}
                aria-label={`Play ${h.title}`}
                data-testid={`home-music-play-${h.id}`}
                className="absolute top-2 right-2 w-9 h-9 grid place-items-center rounded-full bg-[#00AFFF] text-[#020B1F] shadow-[0_0_18px_rgba(0,175,255,0.7)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:scale-110"
              >
                <Play size={14} fill="currentColor" />
              </button>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  COMMUNITY TILES                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
function CommunityTiles() {
  const { t } = useI18n();
  const TILES = [
    { to: "/forum",    label: "Forum",        desc: "Diskutiere mit Fans weltweit",       icon: MessageSquare, color: "#00AFFF" },
    { to: "/chat",     label: "Live-Chat",    desc: "Sofortiger Austausch in Echtzeit",   icon: MessageCircle, color: "#A78BFA" },
    { to: "/fan-map",  label: "Fan-Karte",    desc: "Sieh alle Fans auf der Weltkarte",   icon: MapPin,        color: "#22C55E" },
    { to: "/gallery",  label: "Fan Galerie",  desc: "Bilder, Cover, Backstage-Momente",    icon: ImageIcon,     color: "#F472B6" },
  ];
  return (
    <section data-testid="home-community" className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-12 sm:pt-20">
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#00AFFF] mb-1 inline-flex items-center gap-2">
        <Sparkles size={11} /> {t("home.community_eyebrow", "Community")}
      </div>
      <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl silver-text tracking-tighter mb-6 sm:mb-8">{t("home.community_title", "Werde Teil der Familie")}</h2>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        {TILES.map((tile, i) => (
          <motion.div
            key={tile.to}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.45, delay: i * 0.07 }}
          >
            <Link
              to={tile.to}
              data-testid={`home-community-${tile.label.toLowerCase()}`}
              className="group block glass glass-hover rounded-2xl p-5 sm:p-6 relative overflow-hidden h-full"
            >
              <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-15 blur-2xl transition-all group-hover:opacity-40" style={{ background: tile.color }} />
              <div
                className="w-12 h-12 sm:w-14 sm:h-14 grid place-items-center rounded-2xl mb-4 relative transition-transform group-hover:scale-110"
                style={{ background: `${tile.color}18`, color: tile.color, border: `1px solid ${tile.color}55` }}
              >
                <tile.icon size={22} />
              </div>
              <h3 className="font-display font-black text-lg sm:text-xl text-white mb-1.5">{tile.label}</h3>
              <p className="text-xs sm:text-sm text-white/55 font-body mb-4">{tile.desc}</p>
              <div className="inline-flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-wider" style={{ color: tile.color }}>
                {t("home.community_open", "Öffnen")} <ArrowRight size={11} className="transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  HOME                                                                       */
/* ────────────────────────────────────────────────────────────────────────── */
export default function Home() {
  const [sections, setSections] = useState(null);  // null = loading, [] = use fallback
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/homepage/sections`)
      .then((r) => { if (alive) setSections(r.data?.sections || []); })
      .catch(() => { if (alive) setSections([]); });
    return () => { alive = false; };
  }, []);

  // Map admin-configured section types to renderers (reuses existing components).
  const renderSection = (s, idx) => {
    const key = `${s.id || s.type}-${idx}`;
    const cfg = s.config || {};
    switch (s.type) {
      case "hero":
        return <Hero key={key} />;
      case "community_stats":
        return (
          <React.Fragment key={key}>
            <HomeDashboard />
            <LiveStats />
          </React.Fragment>
        );
      case "news_grid":
        return <NewsSection key={key} />;
      case "discography_strip":
        return <MusicHighlights key={key} />;
      case "tour_upcoming":
        return (
          <Suspense key={key} fallback={<div className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-12"><SectionFallback height={300} /></div>}>
            <HomeEvents />
          </Suspense>
        );
      case "gallery_strip":
        return (
          <Suspense key={key} fallback={<div className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-12"><SectionFallback height={260} /></div>}>
            <HomeNewMembers />
          </Suspense>
        );
      case "games_strip":
        return <CommunityTiles key={key} />;
      case "custom_html":
        return cfg.html ? (
          <section key={key} className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-10" data-testid={`home-section-custom-${s.id}`}>
            <div className="glass rounded-2xl p-5" dangerouslySetInnerHTML={{ __html: cfg.html }} />
          </section>
        ) : null;
      default:
        // Unknown type → render a generic CTA card so admin sees something.
        return (
          <section key={key} className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-10" data-testid={`home-section-generic-${s.id}`}>
            <div className="glass rounded-2xl p-5 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#00AFFF] mb-1">{s.type}</div>
                <h3 className="font-display font-black text-xl sm:text-2xl text-white">{s.title || s.type}</h3>
                {s.subtitle && <p className="text-white/55 text-xs sm:text-sm font-body mt-1">{s.subtitle}</p>}
              </div>
              {s.link && (
                <Link to={s.link} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#00AFFF] text-[#020B1F] font-display font-black uppercase tracking-widest text-[11px]">
                  {s.link_label || "Open"} <ArrowRight size={12} />
                </Link>
              )}
            </div>
          </section>
        );
    }
  };

  // Fallback: legacy hardcoded layout while loading OR when DB returns empty.
  const useFallback = !sections || sections.length === 0;

  return (
    <div className="overflow-x-hidden" data-testid="home-page">
      {useFallback ? (
        <>
          <Hero />
          <HomeDashboard />
          <LiveStats />
          <NewsSection />
          <MusicHighlights />
          <CommunityTiles />
          <Suspense fallback={<div className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-12"><SectionFallback height={420} /></div>}>
            <HomeFanMap />
          </Suspense>
          <Suspense fallback={<div className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-12"><SectionFallback height={260} /></div>}>
            <HomeNewMembers />
          </Suspense>
          <Suspense fallback={<div className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-12"><SectionFallback height={300} /></div>}>
            <HomeEvents />
          </Suspense>
        </>
      ) : (
        sections.map(renderSection)
      )}

      {/* Share footer — always at the bottom */}
      <section className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-12 sm:pt-20 pb-4 sm:pb-8">
        <div className="glass rounded-2xl p-5 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#00AFFF] mb-1">Spread The Blue</div>
            <h3 className="font-display font-black text-xl sm:text-2xl text-white">Erzähl deinen Freunden von Eiffel 65</h3>
            <p className="text-white/55 text-xs sm:text-sm font-body mt-1">Teile diese Seite — je größer die Community, desto besser die Party.</p>
          </div>
          <ShareButtons title="EIFFEL 65 — The Fan Community" dataTestId="home-share" />
        </div>
      </section>
    </div>
  );
}
